import { createRootRoute, createRoute, createRouter, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { helpdeskTenantSearch } from "../../admin/web/helpdesk-tenant-search.ts";
import { approvalListSearch } from "../../approvals/web/approval-list-search.ts";
import { approvalDecisionSearch } from "../../approvals/web/decision-link.ts";
import { ExternalLayout } from "../../external/web/ExternalLayout.tsx";
import { externalRequestsSearch } from "../../external/web/tracking.ts";
import { gatesSearch } from "../../gates/web/gates-search.ts";
import { helpdeskFormsSearch } from "../../helpdesk/web/helpdesk-form-search.ts";
import { adminManagedFieldSearch, managedFieldSearch } from "../../helpdesk/web/managed-field-search.ts";
import { requestDetailSearch } from "../../helpdesk/web/request-detail-search.ts";
import { helpdeskRequestsSearch } from "../../helpdesk/web/request-list-search.ts";
import { tenantPath } from "../tenant-paths.ts";
import { Landing } from "./Landing.tsx";
import { RootLayout } from "./RootLayout.tsx";

const ExternalRequests = lazyRouteComponent(() => import("../../external/web/ExternalRequests.tsx"), "ExternalRequests");
const ExternalRequest = lazyRouteComponent(() => import("../../external/web/ExternalRequest.tsx"), "ExternalRequest");
const ExternalHelpdesk = lazyRouteComponent(() => import("../../external/web/ExternalLayout.tsx"), "ExternalHelpdesk");
const ExternalCategory = lazyRouteComponent(() => import("../../external/web/ExternalCategory.tsx"), "ExternalCategory");
const ExternalForm = lazyRouteComponent(() => import("../../external/web/ExternalForm.tsx"), "ExternalForm");
const ExternalAccount = lazyRouteComponent(() => import("../../external/web/ExternalLayout.tsx"), "ExternalAccount");

const Profile = lazyRouteComponent(() => import("../../user/web/Profile.tsx"), "Profile");
const TimelinePage = lazyRouteComponent(() => import("../../timeline/web/TimelinePage.tsx"), "TimelinePage");
const Approvals = lazyRouteComponent(() => import("../../approvals/web/Approvals.tsx"), "Approvals");
const ApprovalHistory = lazyRouteComponent(() => import("../../approvals/web/ApprovalHistory.tsx"), "ApprovalHistory");
const TeamSetup = lazyRouteComponent(() => import("../../approvals/web/TeamSetup.tsx"), "TeamSetup");
const ApprovalDetail = lazyRouteComponent(() => import("../../approvals/web/ApprovalDetail.tsx"), "ApprovalDetail");
const ApprovalEmailAction = lazyRouteComponent(() => import("../../approvals/web/ApprovalEmailAction.tsx"), "ApprovalEmailAction");
const AdminHome = lazyRouteComponent(() => import("../../admin/web/AdminHome.tsx"), "AdminHome");
const FailureReporting = lazyRouteComponent(() => import("../../admin/web/FailureReporting.tsx"), "FailureReporting");
const AdminDeliveries = lazyRouteComponent(() => import("../../admin/web/AdminDeliveries.tsx"), "AdminDeliveries");
const CaretakerGuide = lazyRouteComponent(() => import("../../admin/web/CaretakerGuide.tsx"), "CaretakerGuide");
const AdminTeams = lazyRouteComponent(() => import("../../admin/web/AdminTeams.tsx"), "AdminTeams");
const HelpdeskTenantPage = lazyRouteComponent(() => import("../../admin/web/HelpdeskWorkspace.tsx"), "HelpdeskTenantPage");
const HelpdeskCategoryPage = lazyRouteComponent(() => import("../../admin/web/HelpdeskWorkspace.tsx"), "HelpdeskCategoryPage");
const AdminCloudInfrastructure = lazyRouteComponent(() => import("../../admin/web/AdminCloudInfrastructure.tsx"), "AdminCloudInfrastructure");
const AdminWebhooks = lazyRouteComponent(() => import("../../admin/web/AdminWebhooks.tsx"), "AdminWebhooks");
const AdminConnectedApps = lazyRouteComponent(() => import("../../admin/web/AdminConnectedApps.tsx"), "AdminConnectedApps");
const HelpdeskForms = lazyRouteComponent(() => import("../../helpdesk/web/Forms.tsx"), "HelpdeskForms");
const HelpdeskFormEditor = lazyRouteComponent(() => import("../../helpdesk/web/FormEditor.tsx"), "HelpdeskFormEditor");
const HelpdeskReusableFields = lazyRouteComponent(() => import("../../helpdesk/web/FormComponents.tsx"), "HelpdeskReusableFields");
const HelpdeskReusableFieldEditor = lazyRouteComponent(() => import("../../helpdesk/web/FormComponents.tsx"), "HelpdeskReusableFieldEditor");
const HelpdeskIntegrations = lazyRouteComponent(() => import("../../helpdesk/web/FormComponents.tsx"), "HelpdeskIntegrations");
const HelpdeskMemberSubcategories = lazyRouteComponent(() => import("../../helpdesk/web/Subcategories.tsx"), "HelpdeskMemberSubcategories");
const AdminManagedFields = lazyRouteComponent(() => import("../../helpdesk/web/ManagedFields.tsx"), "AdminManagedFields");
const AdminManagedFieldEditor = lazyRouteComponent(() => import("../../helpdesk/web/ManagedFields.tsx"), "AdminManagedFieldEditor");
const HelpdeskCatalog = lazyRouteComponent(() => import("../../helpdesk/web/Catalog.tsx"), "HelpdeskCatalog");
const HelpdeskCategory = lazyRouteComponent(() => import("../../helpdesk/web/Category.tsx"), "HelpdeskCategory");
const HelpdeskPublishedForm = lazyRouteComponent(() => import("../../helpdesk/web/PublishedForm.tsx"), "HelpdeskPublishedForm");
const HelpdeskRequestReceipt = lazyRouteComponent(() => import("../../helpdesk/web/RequestReceipt.tsx"), "HelpdeskRequestReceipt");
const DocumentSharing = lazyRouteComponent(() => import("../../helpdesk/web/DocumentSharing.tsx"), "DocumentSharing");
const DocumentSharingList = lazyRouteComponent(() => import("../../helpdesk/web/DocumentSharingList.tsx"), "DocumentSharingList");
const HelpdeskAutomationRecovery = lazyRouteComponent(() => import("../../helpdesk/web/AutomationRecovery.tsx"), "HelpdeskAutomationRecovery");
const HelpdeskRequests = lazyRouteComponent(() => import("../../helpdesk/web/Requests.tsx"), "HelpdeskRequests");
const ApiDocumentation = lazyRouteComponent(() => import("../../helpdesk/web/ApiDocumentationPage.tsx"), "ApiDocumentationPage");
const WebhookReceiverGuide = lazyRouteComponent(() => import("../../helpdesk/web/WebhookReceiverGuidePage.tsx"), "WebhookReceiverGuidePage");
const Gates = lazyRouteComponent(() => import("../../gates/web/Gates.tsx"), "Gates");
const NewGateValidation = lazyRouteComponent(() => import("../../gates/web/ValidationEditor.tsx"), "NewGateValidation");
const EditGateValidation = lazyRouteComponent(() => import("../../gates/web/ValidationEditor.tsx"), "EditGateValidation");
const AdminTeamDetail = lazyRouteComponent(() => import("../../admin/web/AdminTeamDetail.tsx"), "AdminTeamDetail");

const rootRoute = createRootRoute({ component: RootLayout });
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Landing,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: Profile,
});

const timelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/timeline",
  component: TimelinePage,
});
const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals",
  validateSearch: approvalListSearch,
  component: Approvals,
});

const approvalHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals/history",
  validateSearch: approvalListSearch,
  component: ApprovalHistory,
});

const approvalTeamSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals/team-setup",
  component: TeamSetup,
});

const approvalDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals/$id",
  validateSearch: approvalDecisionSearch,
  component: ApprovalDetail,
});

const approvalEmailActionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approvals/email-action",
  component: ApprovalEmailAction,
});

const adminHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminHome,
});

const caretakerGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/guide",
  component: CaretakerGuide,
});

const failureReportingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/failure-reports", component: FailureReporting });

const adminDeliveriesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/deliveries", component: AdminDeliveries });

const adminTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/teams",
  component: AdminTeams,
});

const adminHelpdeskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/helpdesk",
  validateSearch: helpdeskTenantSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/admin/helpdesk/tenants", search });
  },
});

const adminHelpdeskTenantsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/helpdesk/tenants", validateSearch: helpdeskTenantSearch, component: HelpdeskTenantPage });
const adminHelpdeskCategoriesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/helpdesk/categories", validateSearch: helpdeskTenantSearch, component: HelpdeskCategoryPage });

const adminCloudInfrastructureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/helpdesk/cloud-infrastructure",
  component: AdminCloudInfrastructure,
});

const adminWebhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/webhooks",
  component: AdminWebhooks,
});

const adminConnectedAppsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/connected-apps",
  component: AdminConnectedApps,
});

const helpdeskFormsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/manage/forms",
  validateSearch: helpdeskFormsSearch,
  component: HelpdeskForms,
});

const helpdeskFormEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/manage/forms/$id",
  component: HelpdeskFormEditor,
});

const helpdeskReusableFieldsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/manage/components/reusable-fields",
  validateSearch: managedFieldSearch,
  component: HelpdeskReusableFields,
});
const helpdeskReusableFieldEditorRoute = createRoute({ getParentRoute: () => rootRoute, path: "/helpdesk/manage/components/reusable-fields/$id", component: HelpdeskReusableFieldEditor });
const helpdeskIntegrationsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/helpdesk/manage/components/integrations", component: HelpdeskIntegrations });
const helpdeskSubcategoriesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/helpdesk/manage/subcategories", component: HelpdeskMemberSubcategories });
const adminManagedFieldsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/helpdesk/fields", validateSearch: adminManagedFieldSearch, component: AdminManagedFields });
const adminManagedFieldEditorRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/helpdesk/fields/$id", component: AdminManagedFieldEditor });

const helpdeskCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk",
  component: HelpdeskCatalog,
  validateSearch: helpdeskTenantSearch,
});

const helpdeskCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/categories/$departmentId",
  component: HelpdeskCategory,
  validateSearch: helpdeskTenantSearch,
});

const helpdeskPublishedFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/forms/$versionId",
  component: HelpdeskPublishedForm,
  validateSearch: helpdeskTenantSearch,
});

const helpdeskRequestReceiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/requests/$requestId",
  validateSearch: requestDetailSearch,
  component: HelpdeskRequestReceipt,
});

const helpdeskAutomationRecoveryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/automation/$targetId",
  component: HelpdeskAutomationRecovery,
});

const webhookReceiverGuideRoute = createRoute({ getParentRoute: () => rootRoute, path: "/helpdesk/docs/webhook-receiver", component: WebhookReceiverGuide });
const apiDocumentationRoute = createRoute({ getParentRoute: () => rootRoute, path: "/developers/api", component: ApiDocumentation });
const helpdeskRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/helpdesk/requests",
  validateSearch: helpdeskRequestsSearch,
  component: HelpdeskRequests,
});

const documentSharingListRoute = createRoute({ getParentRoute: () => rootRoute, path: "/document-sharing", component: DocumentSharingList });

const documentSharingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/document-sharing/$issueId",
  component: DocumentSharing,
});

const gatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gates",
  validateSearch: gatesSearch,
  component: Gates,
});

const newGateValidationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gates/new",
  component: NewGateValidation,
});

const editGateValidationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/gates/$id/edit",
  component: EditGateValidation,
});

const adminTeamDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/teams/$id",
  component: AdminTeamDetail,
});

const externalRootRoute = createRootRoute({ component: ExternalLayout });
const externalIndexRoute = createRoute({
  getParentRoute: () => externalRootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/helpdesk" });
  },
});
const externalHelpdeskRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/helpdesk", component: ExternalHelpdesk });
const externalCategoryRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/helpdesk/categories/$departmentId", component: ExternalCategory });
const externalFormRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/helpdesk/forms/$versionId", component: ExternalForm });
const externalRequestsRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/helpdesk/requests", component: ExternalRequests, validateSearch: externalRequestsSearch });
const externalRequestRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/helpdesk/requests/$requestId", validateSearch: requestDetailSearch, component: ExternalRequest });
const externalAccountRoute = createRoute({ getParentRoute: () => externalRootRoute, path: "/profile", component: ExternalAccount });

const externalRouteTree = externalRootRoute.addChildren([
  externalIndexRoute,
  externalHelpdeskRoute,
  externalRequestsRoute,
  externalRequestRoute,
  externalAccountRoute,
  externalCategoryRoute,
  externalFormRoute,
]);

const internalRouteTree = rootRoute.addChildren([
  landingRoute,
  profileRoute,
  timelineRoute,
  approvalsRoute,
  approvalHistoryRoute,
  approvalTeamSetupRoute,
  approvalEmailActionRoute,
  approvalDetailRoute,
  gatesRoute,
  newGateValidationRoute,
  editGateValidationRoute,
  adminHomeRoute,
  caretakerGuideRoute,
  failureReportingRoute,
  adminDeliveriesRoute,
  adminTeamsRoute,
  adminHelpdeskRoute,
  adminHelpdeskTenantsRoute,
  adminHelpdeskCategoriesRoute,
  adminCloudInfrastructureRoute,
  adminWebhooksRoute,
  adminConnectedAppsRoute,
  helpdeskFormsRoute,
  helpdeskFormEditorRoute,
  helpdeskReusableFieldsRoute,
  helpdeskReusableFieldEditorRoute,
  helpdeskIntegrationsRoute,
  helpdeskSubcategoriesRoute,
  adminManagedFieldsRoute,
  adminManagedFieldEditorRoute,
  helpdeskCatalogRoute,
  helpdeskCategoryRoute,
  helpdeskPublishedFormRoute,
  helpdeskRequestsRoute,
  webhookReceiverGuideRoute,
  apiDocumentationRoute,
  documentSharingListRoute,
  documentSharingRoute,
  helpdeskRequestReceiptRoute,
  helpdeskAutomationRecoveryRoute,
  adminTeamDetailRoute,
]);

const routerFor = (slug: string): { basepath: string } => ({ basepath: tenantPath(slug) });

const createInternalRouter = (slug: string) => createRouter({ routeTree: internalRouteTree, ...routerFor(slug) });

export type AppRouter = ReturnType<typeof createInternalRouter>;

export type PortalTreeKind = "internal" | "external";

// The tree is fixed per page load: the router's basepath is the tenant slug and the tree follows the session kind.
// Moving between tenants is a full navigation. External pages are a subset of the internal paths, so one registered type serves both.
export const createAppRouter = (slug: string, kind: PortalTreeKind): AppRouter =>
  kind === "external" ? (createRouter({ routeTree: externalRouteTree, ...routerFor(slug) }) as unknown as AppRouter) : createInternalRouter(slug);

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
